import { Eye, EyeOff, ShieldCheck, UserRound } from 'lucide-react';
import QRCode from 'qrcode';
import { FormEvent, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSearchParams } from '../lib/router';
import type { Admin } from '../lib/types';

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: 'Super administrador',
  OPERATIONS: 'Operações',
  COMPLIANCE: 'Compliance',
  SUPPORT: 'Suporte',
  AUDITOR: 'Auditoria',
};

const TABS = [
  { key: 'profile', label: 'Perfil', icon: UserRound },
  { key: 'security', label: 'Segurança', icon: ShieldCheck },
] as const;

export function AccountPage() {
  const { admin } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'security' ? 'security' : 'profile';

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">A minha conta</span>
          <h1>{admin?.fullName}</h1>
          <p>
            {admin?.email} · {roleLabels[admin?.role ?? ''] ?? admin?.role}
          </p>
        </div>
      </div>

      <div className="account-tabs" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`account-tab ${tab === key ? 'account-tab--active' : ''}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('tab', key);
              setParams(next);
            }}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' ? <ProfileTab /> : <SecurityTab />}
    </div>
  );
}

function ProfileTab() {
  const { admin, refreshAdmin } = useAuth();

  return (
    <>
      <ProfileForm admin={admin} onSaved={refreshAdmin} />
      <PasswordForm />
    </>
  );
}

function ProfileForm({
  admin,
  onSaved,
}: {
  admin: Admin | null;
  onSaved: () => Promise<void>;
}) {
  const [fullName, setFullName] = useState(admin?.fullName ?? '');
  const [email, setEmail] = useState(admin?.email ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      await api('/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ fullName, email }),
      });
      await onSaved();
      setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? 'Já existe uma conta com este email.'
          : 'Não foi possível guardar as alterações.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel__heading">
        <div>
          <h2>Dados pessoais</h2>
          <p>Nome e email usados para iniciar sessão no backoffice.</p>
        </div>
      </div>
      <form className="panel-body" onSubmit={submit}>
        <label>
          Nome completo
          <input
            value={fullName}
            onChange={(event) => {
              setFullName(event.target.value);
              setSaved(false);
            }}
            required
            minLength={2}
            maxLength={120}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setSaved(false);
            }}
            required
          />
        </label>
        {error && <div className="form-error">{error}</div>}
        {saved && !error && <div className="form-success">Alterações guardadas.</div>}
        <div className="modal__actions modal__actions--start">
          <button className="button button--primary" disabled={busy}>
            {busy ? 'A guardar…' : 'Guardar alterações'}
          </button>
        </div>
      </form>
    </section>
  );
}

function PasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSaved(false);
    if (newPassword !== confirmPassword) {
      setError('As palavras-passe não coincidem.');
      return;
    }
    setBusy(true);
    try {
      await api('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Palavra-passe atual incorreta.'
          : 'Não foi possível alterar a palavra-passe.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel__heading">
        <div>
          <h2>Palavra-passe</h2>
          <p>As outras sessões ativas terminam ao alterar a palavra-passe.</p>
        </div>
      </div>
      <form className="panel-body" onSubmit={submit}>
        <label>
          Palavra-passe atual
          <div className="password-field">
            <input
              type={showPasswords ? 'text' : 'password'}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPasswords((value) => !value)}
              aria-label={showPasswords ? 'Ocultar palavras-passe' : 'Mostrar palavras-passe'}
            >
              {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        <label>
          Nova palavra-passe
          <input
            type={showPasswords ? 'text' : 'password'}
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
            minLength={12}
          />
        </label>
        <label>
          Confirmar nova palavra-passe
          <input
            type={showPasswords ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
            minLength={12}
          />
        </label>
        {error && <div className="form-error">{error}</div>}
        {saved && !error && <div className="form-success">Palavra-passe alterada.</div>}
        <div className="modal__actions modal__actions--start">
          <button className="button button--primary" disabled={busy}>
            {busy ? 'A alterar…' : 'Alterar palavra-passe'}
          </button>
        </div>
      </form>
    </section>
  );
}

function SecurityTab() {
  const { admin, refreshAdmin } = useAuth();
  const [enrolling, setEnrolling] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function startEnrollment() {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ secret: string; otpauthUrl: string }>(
        '/auth/mfa/setup',
        { method: 'POST' },
      );
      setSecret(result.secret);
      setQrDataUrl(await QRCode.toDataURL(result.otpauthUrl));
      setEnrolling(true);
    } catch {
      setError('Não foi possível iniciar a configuração. Tenta novamente.');
    } finally {
      setBusy(false);
    }
  }

  function cancelEnrollment() {
    setEnrolling(false);
    setSecret('');
    setQrDataUrl('');
    setCode('');
    setError('');
  }

  async function confirmEnable(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/auth/mfa/enable', {
        method: 'POST',
        body: JSON.stringify({ secret, code }),
      });
      cancelEnrollment();
      await refreshAdmin();
    } catch {
      setError('Código inválido. Confirma a hora do telemóvel e tenta novamente.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel__heading">
        <div>
          <h2>Autenticação de dois fatores</h2>
          <p>Protege esta conta com um código de uma aplicação de autenticação.</p>
        </div>
        <ShieldCheck size={21} />
      </div>

      <div className="panel-body">
        {admin?.mfaEnabled ? (
          <p>
            A autenticação de dois fatores está <strong>ativa</strong> nesta conta.
            É obrigatória para todos os administradores e não pode ser desativada.
          </p>
        ) : !enrolling ? (
          <>
            <p>
              A autenticação de dois fatores está <strong>desativada</strong>.
              É obrigatória para todas as contas do backoffice.
            </p>
            <button
              className="button button--primary"
              onClick={startEnrollment}
              disabled={busy}
            >
              {busy ? 'A preparar…' : 'Ativar MFA'}
            </button>
            {error && <div className="form-error">{error}</div>}
          </>
        ) : (
          <form className="mfa-setup" onSubmit={confirmEnable}>
            <p>1. Digitaliza este código com a tua aplicação de autenticação (Google Authenticator, 1Password, Authy, etc.):</p>
            <img className="mfa-qr" src={qrDataUrl} alt="Código QR de configuração MFA" />
            <p>Ou insere manualmente: <code>{secret}</code></p>
            <label>
              2. Código de 6 dígitos
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
                minLength={6}
                maxLength={8}
                inputMode="numeric"
                autoFocus
              />
            </label>
            {error && <div className="form-error">{error}</div>}
            <div className="modal__actions">
              <button type="button" className="button button--secondary" onClick={cancelEnrollment}>
                Cancelar
              </button>
              <button className="button button--primary" disabled={busy}>
                {busy ? 'A confirmar…' : 'Confirmar e ativar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
